import React, { forwardRef, useEffect, useState } from 'react';
import styles from './GenericOrganisationSearch.module.scss';
import orgSearchService from './GenericOrganisationService';
import { validate, isCompleted } from '../Utils/Validation';
import { Notification, ComponentLoader, Button, FormInput } from '@snsw/react-component-library';
import abnAcnService from '../OrganisationAbnAcn/OrganisationAbnAcnService';
import styled from 'styled-components';

const Label = styled.div`
  width: 100%;
  color: #242934;
  font-weight: 500;
`;

const GenericOrganisationSearch = ({ detailStyle = 1, ...props }, ref) => {
  const [isValid, setIsValid] = useState(false);
  const [businessName, setBusinessName] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('false');
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [currentNumber, setCurrentNumber] = useState(props.number || '');

  const clearResult = () => {
    setIsValid(undefined);
    setLoaded(false);
    props.setData('');
  };

  useEffect(() => {
    setShowResult(false);
    clearResult();
  }, [currentNumber]);

  useEffect(() => {
    if (currentNumber) {
      handleLookup(currentNumber);
    }
  }, []);

  useEffect(() => {
    if (props.onValidationChange) {
      props.onValidationChange(!!loaded && isValid);
    }
  }, [showResult, loaded, isValid]);

  const retrieveValue = (searchValue) => {
    setLoading(true);
    setLoaded(false);
    setIsValid(undefined);
    setBusinessName('');
    setRegistrationNumber('');

    let initialPromise = new Promise((resolve) => resolve(true));
    if (props.regoType === 'ABN') {
      initialPromise = abnAcnService.search(`${props.abnUrl}`, searchValue);
    } else if (props.regoType === 'ACN') {
      initialPromise = abnAcnService.search(`${props.acnUrl}`, searchValue);
    }
    // https://siebelservice.uat.onegov.nsw.gov.au/api/V1/Database/GetLicenceDetailsByRegistrationNumber?registrationNumber=38600126128&licenceTypeId=1-2XQ-2
    initialPromise
      .then((r) => {
        if (props.regoType === 'ABN' || props.regoType === 'ACN') {
          if (props.setAbnAcnData) {
            props.setAbnAcnData(r?.data);
          }
          return r?.data?.length > 0;
        } else {
          return r;
        }
      })
      .then((found) =>
        orgSearchService
          .search(
            `${props.url}?registrationNumber=${searchValue}&regoType=${props.regoType}&isAbnAcnExists=${
              found ? 'true' : 'false'
            }`
          )
          .then((response) => {
            setShowResult(true);
            setErrors({
              ...errors,
              apiError: false,
            });
            setIsValid(!!response.data.isValid);
            setBusinessName(response.data.businessName);
            setRegistrationNumber(response.data.registrationNumber);
            if (props.setData) {
              props.setData(response.data);
            }
            setLoaded(true);
          })
      )
      .catch(() => {
        console.log('The reject');
        setShowResult(false);
        setErrors({
          ...errors,
          apiError: true,
        });
        setIsValid(false);
        if (props.setData) {
          props.setData([]);
        }
        setLoaded(false);
      })
      .then(() => {
        setLoading(false);
      });
  };

  const handleLookup = () => retrieveValue(currentNumber);

  ///Rules for trading name: 1. show 'Same as above' 1st. 2. combine the tradingName and businessName together from ABR.
  const transformNames = (businessNames, tradingNames) => {
    return [defaultTradingName]
      .concat((businessNames || []).map((label) => ({ value: label, label })))
      .concat((tradingNames || []).map((label) => ({ value: label, label })));
  };

  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState({
    apiError: false,
    currentNumber: false,
  });
  const validators = [
    {
      fieldName: 'apiError',
      fieldValue: errors,
      isValid: ({ apiError }) => apiError,
    },
    {
      fieldName: 'currentNumber',
      fieldValue: currentNumber,
      isValid: (v) => isCompleted(v) && isValid,
    },
  ];

  useEffect(() => {
    if (currentNumber.length >= 1) {
      setShowErrors(false);
    }
  }, [currentNumber]);

  if (ref) {
    ref.current = {
      validate: () => {
        const isValid = validate(validators, setErrors);
        setShowErrors(!isValid);
      },
      clear: clearResult,
    };
  }

  return (
    <div className={styles.mainContainer}>
      {props.title && <Label>{props.title}</Label>}
      <div className={`row ${styles.searchBox}`}>
        <div className={styles.searchInput}>
          <FormInput
            id={'currentNumberInput'}
            placeholder={props.type}
            value={currentNumber}
            onChange={async (e) => {
              const isAbnAcn = props.regoType === 'ABN' || props.regoType === 'ACN';
              const regex = isAbnAcn ? /\D/g : /\W\ /g;
              const maxChars = isAbnAcn ? 11 : 30;
              let numericVals = e.target.value.replace(regex, '');
              if (numericVals.length > maxChars) {
                numericVals = numericVals.substring(0, maxChar);
              }
              setCurrentNumber(numericVals);
              if (props.setNumber) {
                props.setNumber(numericVals);
              }
            }}
          />
        </div>
        <div className={styles.searchButton}>
          <Button
            theme="secondary"
            disabled={!currentNumber || !props.regoType}
            onClick={() => {
              if (currentNumber && props.regoType) {
                handleLookup();
              }
            }}
            onKeyDown={(e) => {
              if (currentNumber && props.regoType && e.keyCode === 13) {
                handleLookup();
              }
            }}
          >
            Search
          </Button>
        </div>
      </div>
      {showErrors && errors.apiError ? (
        <div className={'row'}>
          <div className={`col-12`}>
            <Notification variant="error" title={`Registration Number is required.`} />
          </div>
        </div>
      ) : null}
      {loading && <ComponentLoader className={`${styles.loading} ${props.title ? styles.loadingWithTitle : ''}`} />}
      {showResult && !!loaded && isValid ? (
        props.showDetailOnSuccess ? (
          props.detailStyle === 1 ? (
            <div className={'row'}>
              <div className={`col-12`}>
                <div className={styles.detail} id={'abnAcnSearchResult'}>
                  <div className={styles.detailLine}>
                    <div className={styles.detailLeft}>Business name</div>
                    <div className={styles.detailRight}>{businessName}</div>
                  </div>
                  <div className={styles.detailLine}>
                    <div className={styles.detailLeft}>Registration Number</div>
                    <div className={styles.detailRight}>{registrationNumber}</div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className={'row'}>
              <div className={`col-12`}>
                <div className={styles.detail} id={'abnAcnSearchResult'}>
                  <div className={styles.titleLine}>
                    <h3 className={styles.titleLeft}>{businessName}</h3>
                    <div className={styles.titleRight}>
                      <a
                        href={'#'}
                        onClick={(e) => {
                          e.preventDefault();
                          clearResult(true);
                        }}
                      >
                        Clear
                      </a>
                    </div>
                  </div>
                  <div className={styles.detailLine}>
                    <div className={styles.detailLeft}>{props.title}</div>
                    <div className={styles.detailRight}>{registrationNumber}</div>
                  </div>
                </div>
              </div>
            </div>
          )
        ) : (
          <Notification variant="success" title={`Record matched successfully.`} />
        )
      ) : null}
      {!!loaded && !isValid && (typeof props.showError === 'undefined' || props.showError === true) ? (
        <div className={'row'}>
          <div className={`col-12`}>
            <Notification
              variant="error"
              title={
                props?.errors && props?.errors[props.regoType.toLowerCase()]
                  ? props?.errors[props.regoType.toLowerCase()]
                  : `The number entered is not a valid ${props.regoType}.`
              }
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default forwardRef(GenericOrganisationSearch);
